import { Modal as MantineModal } from '@mantine/core';
import { ReactNode } from 'react';

import styles from './_DialogShell.module.css';

export interface DialogShellProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Callback fired when the dialog is closed (overlay click, escape, close button) */
  onClose: () => void;
  /** Whether the dialog is centered vertically */
  isCentered?: boolean;
  /** Dialog content width */
  size?: string | number;
  /** Whether to close the dialog when clicking outside */
  shouldCloseOnClickOutside?: boolean;
  /** Whether to close the dialog when pressing Escape */
  shouldCloseOnEscape?: boolean;
  /** Extra class applied to the inner root container (padding / gap live here) */
  className?: string;
  /** Extra class applied to Mantine's Modal.Content (e.g. to cap height for scroll) */
  contentClassName?: string;
  children: ReactNode;
}

/**
 * Private shared shell for the V2 Dialog family (AlertDialog, ConfirmationDialog,
 * ActionDialog). Wraps Mantine `Modal` with the common surface styling
 * (12px radius, message shadow, no default padding) and dismiss behavior.
 * Not exported from the design system — compose the public dialogs instead.
 */
export function DialogShell({
  isOpen,
  onClose,
  isCentered = true,
  size,
  shouldCloseOnClickOutside,
  shouldCloseOnEscape,
  className,
  contentClassName,
  children,
}: DialogShellProps) {
  const classNames = {
    content: contentClassName
      ? `${styles.modalContent} ${contentClassName}`
      : styles.modalContent,
    body: styles.modalBody,
  };

  return (
    <MantineModal
      opened={isOpen}
      onClose={onClose}
      withCloseButton={false}
      centered={isCentered}
      size={size}
      closeOnClickOutside={shouldCloseOnClickOutside}
      closeOnEscape={shouldCloseOnEscape}
      classNames={classNames}
    >
      <div className={`${styles.root} ${className ?? ''}`}>{children}</div>
    </MantineModal>
  );
}

DialogShell.displayName = 'DialogShell';
